import { useEffect, useState, useCallback } from "react";
import { api, fileSrc } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarClock, Loader2, ExternalLink, Copy, Check, Plus, Trash2, Star, Upload, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { usePageGuard } from "@/lib/unsavedGuard";

const DAYS = [[1, "Lundi"], [2, "Mardi"], [3, "Mercredi"], [4, "Jeudi"], [5, "Vendredi"], [6, "Samedi"], [7, "Dimanche"]];
const FIELDS = [["vehicle", "Véhicule (marque, modèle, immatriculation)"], ["email", "Email"], ["address", "Adresse d'intervention"], ["company", "Entreprise"], ["service", "Choix de la prestation"], ["comment", "Commentaire"]];
const SOCIALS = [["facebook", "Facebook"], ["instagram", "Instagram"], ["tiktok", "TikTok"], ["twitter", "X / Twitter"], ["linkedin", "LinkedIn"], ["youtube", "YouTube"]];
const PAYMENTS = ["Espèces", "Carte bancaire", "Virement bancaire", "Chèque", "Wero"];

export default function Booking() {
  const guardRef = usePageGuard();
  const [link, setLink] = useState(null);
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [otherPay, setOtherPay] = useState("");
  const [holidays, setHolidays] = useState([]);
  const [year] = useState(new Date().getFullYear());

  const loadSettings = useCallback(() => {
    api.get("/my-public-link").then((r) => setLink(r.data)).catch(() => {});
    api.get("/settings").then((r) => {
      const d = r.data || {};
      setS({
        public_page_enabled: d.public_page_enabled !== false,
        business_type: d.business_type || "",
        public_subtitle: d.public_subtitle || "",
        booking_fields: { vehicle: true, email: true, address: false, company: false, comment: true, service: true, ...(d.booking_fields || {}) },
        open_days: d.open_days || [1, 2, 3, 4, 5],
        social: d.social || {},
        payment_methods: d.payment_methods || [],
        directory_opt_in: !!d.directory_opt_in,
        directory_city: d.directory_city || "",
        directory_services: d.directory_services || "",
        directory_description: d.directory_description || "",
        day_hours: d.day_hours || {},
        slot_duration: d.slot_duration || 60,
        manual_slots: d.manual_slots || {},
        booking_fixed_time: d.booking_fixed_time || "",
        worked_holidays: d.worked_holidays || [],
      });
      const others = (d.payment_methods || []).filter((p) => !PAYMENTS.includes(p));
      setOtherPay(others.join(", "));
    }).catch(() => {});
    api.get(`/holidays?year=${year}`).then((r) => setHolidays(r.data.holidays || [])).catch(() => {});
  }, [year]);
  useEffect(() => { loadSettings(); }, [loadSettings]);

  if (!s) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;

  const url = link ? `${window.location.origin}${link.path}` : "";
  const copy = () => { try { navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (e) {} };
  const toggleField = (k) => setS({ ...s, booking_fields: { ...s.booking_fields, [k]: !s.booking_fields[k] } });
  const toggleDay = (d) => setS({ ...s, open_days: s.open_days.includes(d) ? s.open_days.filter((x) => x !== d) : [...s.open_days, d].sort() });
  const togglePay = (p) => setS({ ...s, payment_methods: s.payment_methods.includes(p) ? s.payment_methods.filter((x) => x !== p) : [...s.payment_methods, p] });
  const setSocial = (k, v) => setS({ ...s, social: { ...s.social, [k]: v } });

  const save = async () => {
    setSaving(true);
    const base = s.payment_methods.filter((p) => PAYMENTS.includes(p));
    const others = otherPay.split(",").map((x) => x.trim()).filter(Boolean);
    const payload = { ...s, payment_methods: [...base, ...others] };
    try { await api.put("/settings", payload); toast.success("Configuration enregistrée"); loadSettings(); }
    catch { toast.error("Enregistrement impossible"); } finally { setSaving(false); }
  };

  // ---- day hours helpers ----
  const dayRanges = (d) => s.day_hours[String(d)] || [];
  const setDayRanges = (d, ranges) => setS({ ...s, day_hours: { ...s.day_hours, [String(d)]: ranges } });
  const addRange = (d) => setDayRanges(d, [...dayRanges(d), { start: "09:00", end: "12:00" }]);
  const updRange = (d, i, k, v) => setDayRanges(d, dayRanges(d).map((r, x) => (x === i ? { ...r, [k]: v } : r)));
  const rmRange = (d, i) => setDayRanges(d, dayRanges(d).filter((_, x) => x !== i));
  const manualSlots = (d) => s.manual_slots[String(d)] || [];
  const addManual = (d, t) => { if (t && !manualSlots(d).includes(t)) setS({ ...s, manual_slots: { ...s.manual_slots, [String(d)]: [...manualSlots(d), t].sort() } }); };
  const rmManual = (d, t) => setS({ ...s, manual_slots: { ...s.manual_slots, [String(d)]: manualSlots(d).filter((x) => x !== t) } });

  const toggleWorkedHoliday = (date) => {
    const w = s.worked_holidays.includes(date) ? s.worked_holidays.filter((x) => x !== date) : [...s.worked_holidays, date];
    setS({ ...s, worked_holidays: w });
  };

  return (
    <div ref={guardRef} className="max-w-4xl">
      <PageHeader title="Page de réservation" subtitle="Votre page publique de prise de rendez-vous, personnalisable par métier" icon={CalendarClock}
        actions={<Button onClick={save} disabled={saving} data-testid="booking-save" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Enregistrer</Button>} />

      {/* Lien public (toujours visible) */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4" data-testid="booking-link">
        <div className="font-heading font-semibold text-slate-900 mb-3">Votre lien public</div>
        <p className="text-xs text-slate-500 mb-3">Partagez ce lien (site, Google, Facebook, Instagram, TikTok, WhatsApp, SMS, QR code). Vos clients arrivent directement sur VOTRE page.</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input readOnly value={url} data-testid="booking-url" onFocus={(e) => e.target.select()} className="bg-slate-50 border-slate-200 font-mono text-xs" />
          <div className="flex gap-2 shrink-0">
            <Button type="button" variant="outline" onClick={copy} data-testid="booking-copy" className="border-slate-200">{copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}{copied ? "Copié !" : "Copier"}</Button>
            <Button type="button" onClick={() => window.open(url, "_blank")} disabled={!url} data-testid="booking-open" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"><ExternalLink className="w-4 h-4 mr-1" />Voir ma page</Button>
          </div>
        </div>
        <label className="flex items-center gap-2 mt-3 text-sm text-slate-700">
          <input type="checkbox" data-testid="booking-enabled" checked={s.public_page_enabled} onChange={() => setS({ ...s, public_page_enabled: !s.public_page_enabled })} />
          Page de réservation activée (accessible publiquement)
        </label>
      </div>

      <Tabs defaultValue="page" className="mb-10">
        <TabsList className="flex flex-wrap h-auto bg-slate-100 mb-4">
          <TabsTrigger value="page" data-testid="tab-page">Ma page</TabsTrigger>
          <TabsTrigger value="hours" data-testid="tab-hours">Horaires & créneaux</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar">Fériés & absences</TabsTrigger>
          <TabsTrigger value="gallery" data-testid="tab-gallery">Galerie</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-reviews">Avis</TabsTrigger>
          <TabsTrigger value="faq" data-testid="tab-faq">FAQ</TabsTrigger>
        </TabsList>

        {/* ---------- MA PAGE ---------- */}
        <TabsContent value="page">
          <Card title="Votre métier & votre page" testid="booking-identity">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs text-slate-500">Métier / activité</Label><Input data-testid="booking-business-type" value={s.business_type} onChange={(e) => setS({ ...s, business_type: e.target.value })} placeholder="Ex : coiffure, plomberie, detailing, photographe…" className="bg-slate-50 border-slate-200" /></div>
              <div className="space-y-1.5"><Label className="text-xs text-slate-500">Sous-titre de la page</Label><Input data-testid="booking-subtitle" value={s.public_subtitle} onChange={(e) => setS({ ...s, public_subtitle: e.target.value })} placeholder="Ex : Prise de rendez-vous en ligne" className="bg-slate-50 border-slate-200" /></div>
            </div>
          </Card>

          <Card title="Champs demandés au client" testid="booking-fields">
            <div className="grid sm:grid-cols-2 gap-2">
              {FIELDS.map(([k, lbl]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 cursor-pointer">
                  <input type="checkbox" data-testid={`booking-field-${k}`} checked={!!s.booking_fields[k]} onChange={() => toggleField(k)} />{lbl}
                </label>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-2">Prénom, nom et téléphone sont toujours demandés. Désactivez « Véhicule » si votre métier n'en a pas besoin.</p>
          </Card>

          <Card title="Jours d'ouverture" testid="booking-days">
            <p className="text-xs text-slate-500 mb-3">Les jours décochés apparaissent « fermés » et ne sont pas réservables sur votre page publique.</p>
            <div className="flex flex-wrap gap-2">
              {DAYS.map(([d, lbl]) => {
                const on = s.open_days.includes(d);
                return <button key={d} type="button" data-testid={`booking-day-${d}`} onClick={() => toggleDay(d)} className={`px-3 py-2 rounded-lg text-sm border transition ${on ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-slate-50 border-slate-200 text-slate-400"}`}>{lbl.slice(0, 3)}</button>;
              })}
            </div>
          </Card>

          <Card title="Moyens de paiement acceptés" testid="booking-payments">
            <div className="grid sm:grid-cols-2 gap-2">
              {PAYMENTS.map((p) => (
                <label key={p} className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 cursor-pointer">
                  <input type="checkbox" data-testid={`booking-pay-${p}`} checked={s.payment_methods.includes(p)} onChange={() => togglePay(p)} />{p}
                </label>
              ))}
            </div>
            <div className="space-y-1.5 mt-2"><Label className="text-xs text-slate-500">Autre (séparés par des virgules) — ex. PayPal, carte cadeau</Label><Input data-testid="booking-pay-other" value={otherPay} onChange={(e) => setOtherPay(e.target.value)} className="bg-slate-50 border-slate-200" /></div>
            <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800" data-testid="booking-deposit-config">
              <b>Acompte / paiement en ligne :</b> Configuration nécessaire. Chaque professionnel doit connecter son propre compte de paiement (ex. Stripe Connect). Aucun paiement n'est encaissé tant que ce n'est pas configuré — rien n'est simulé.
            </div>
          </Card>

          <Card title="Réseaux sociaux" testid="booking-social">
            <div className="grid sm:grid-cols-2 gap-3">
              {SOCIALS.map(([k, lbl]) => (
                <div key={k} className="space-y-1.5"><Label className="text-xs text-slate-500">{lbl}</Label><Input data-testid={`booking-social-${k}`} value={s.social[k] || ""} onChange={(e) => setSocial(k, e.target.value)} placeholder="https://…" className="bg-slate-50 border-slate-200" /></div>
              ))}
            </div>
          </Card>

          <Card title="Annuaire des professionnels" testid="booking-directory">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-800 mb-3">
              <input type="checkbox" data-testid="booking-directory-optin" checked={s.directory_opt_in} onChange={() => setS({ ...s, directory_opt_in: !s.directory_opt_in })} />
              Apparaître dans l'annuaire (facultatif)
            </label>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs text-slate-500">Ville / zone</Label><Input data-testid="booking-directory-city" value={s.directory_city} onChange={(e) => setS({ ...s, directory_city: e.target.value })} className="bg-slate-50 border-slate-200" /></div>
              <div className="space-y-1.5"><Label className="text-xs text-slate-500">Spécialités</Label><Input data-testid="booking-directory-services" value={s.directory_services} onChange={(e) => setS({ ...s, directory_services: e.target.value })} className="bg-slate-50 border-slate-200" /></div>
            </div>
            <div className="space-y-1.5 mt-3"><Label className="text-xs text-slate-500">Description</Label><Textarea data-testid="booking-directory-desc" value={s.directory_description} onChange={(e) => setS({ ...s, directory_description: e.target.value })} className="bg-slate-50 border-slate-200" /></div>
          </Card>
          <SaveBtn onClick={save} saving={saving} />
        </TabsContent>

        {/* ---------- HORAIRES & CRÉNEAUX ---------- */}
        <TabsContent value="hours">
          <Card title="Durée d'un créneau" testid="booking-slot-duration">
            <div className="flex items-center gap-3">
              <Label className="text-xs text-slate-500">Durée (minutes)</Label>
              <Select value={String(s.slot_duration)} onValueChange={(v) => setS({ ...s, slot_duration: parseInt(v) })}>
                <SelectTrigger data-testid="slot-duration-select" className="w-32 bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[15, 30, 45, 60, 90, 120, 180, 240].map((m) => <SelectItem key={m} value={String(m)}>{m} min</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </Card>

          <Card title="Heure proposée pour les demandes de RDV" testid="booking-fixed-time">
            <div className="flex flex-wrap items-center gap-3">
              <Label className="text-xs text-slate-500">Heure unique</Label>
              <input type="time" data-testid="booking-fixed-time-input" value={s.booking_fixed_time} onChange={(e) => setS({ ...s, booking_fixed_time: e.target.value })} className="h-10 rounded-md border border-slate-200 bg-slate-50 px-3 text-sm" />
              {s.booking_fixed_time && <button type="button" data-testid="booking-fixed-time-clear" onClick={() => setS({ ...s, booking_fixed_time: "" })} className="text-xs text-slate-500 underline">Proposer tous les créneaux</button>}
            </div>
            <p className="text-[11px] text-slate-400 mt-2">Si une heure est indiquée, le client choisit seulement le jour : cette heure est le seul horaire proposé (les jours fermés ou déjà occupés restent indisponibles). Laissez vide pour proposer tous vos créneaux.</p>
          </Card>

          <Card title="Horaires & créneaux par jour" testid="booking-day-hours">
            <p className="text-xs text-slate-500 mb-3">Pour chaque jour ouvré, définissez une ou plusieurs plages horaires (ex. 9h-12h et 14h-18h). Un jour sans plage ni créneau manuel est considéré fermé à la réservation en ligne.</p>
            <div className="space-y-3">
              {DAYS.map(([d, lbl]) => {
                const open = s.open_days.includes(d);
                return (
                  <div key={d} className={`border rounded-lg p-3 ${open ? "border-slate-200" : "border-slate-100 bg-slate-50 opacity-60"}`} data-testid={`dayhours-${d}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-800">{lbl}{!open && <span className="text-[11px] text-slate-400 ml-2">(jour fermé — activez-le dans « Ma page »)</span>}</span>
                      <Button type="button" size="sm" variant="outline" data-testid={`dayhours-add-${d}`} onClick={() => addRange(d)} className="h-7 text-xs border-slate-200"><Plus className="w-3 h-3 mr-1" />Plage</Button>
                    </div>
                    <div className="space-y-2">
                      {dayRanges(d).map((r, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <input type="time" data-testid={`dayhours-start-${d}-${i}`} value={r.start} onChange={(e) => updRange(d, i, "start", e.target.value)} className="bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-sm" />
                          <span className="text-slate-400 text-sm">→</span>
                          <input type="time" data-testid={`dayhours-end-${d}-${i}`} value={r.end} onChange={(e) => updRange(d, i, "end", e.target.value)} className="bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-sm" />
                          <button type="button" data-testid={`dayhours-rm-${d}-${i}`} onClick={() => rmRange(d, i)} className="text-red-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      ))}
                      {dayRanges(d).length === 0 && <p className="text-[11px] text-slate-400">Aucune plage. Fallback : plage globale d'ouverture.</p>}
                    </div>
                    <ManualSlotEditor day={d} slots={manualSlots(d)} onAdd={(t) => addManual(d, t)} onRemove={(t) => rmManual(d, t)} />
                  </div>
                );
              })}
            </div>
          </Card>
          <SaveBtn onClick={save} saving={saving} />
        </TabsContent>

        {/* ---------- FÉRIÉS & ABSENCES ---------- */}
        <TabsContent value="calendar">
          <Card title={`Jours fériés ${year} (France)`} testid="booking-holidays">
            <p className="text-xs text-slate-500 mb-3">Les jours fériés sont automatiquement bloqués à la réservation. Cochez « Je travaille » pour rouvrir un jour férié précis.</p>
            <div className="space-y-1.5">
              {holidays.map((h) => (
                <label key={h.date} data-testid={`holiday-${h.date}`} className="flex items-center justify-between text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                  <span className="text-slate-700">{h.name} <span className="text-slate-400 text-xs">· {h.date}</span></span>
                  <span className="flex items-center gap-2 text-xs text-slate-600">
                    <input type="checkbox" data-testid={`holiday-worked-${h.date}`} checked={s.worked_holidays.includes(h.date)} onChange={() => toggleWorkedHoliday(h.date)} />
                    Je travaille
                  </span>
                </label>
              ))}
            </div>
            <div className="mt-3"><SaveBtn onClick={save} saving={saving} label="Enregistrer les jours fériés" inline /></div>
          </Card>

          <AbsencesCard />
        </TabsContent>

        {/* ---------- GALERIE ---------- */}
        <TabsContent value="gallery">
          <GalleryCard />
        </TabsContent>

        {/* ---------- AVIS ---------- */}
        <TabsContent value="reviews">
          <ReviewsCard publicUrl={url} />
        </TabsContent>

        {/* ---------- FAQ ---------- */}
        <TabsContent value="faq">
          <FaqCard />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ManualSlotEditor({ day, slots, onAdd, onRemove }) {
  const [t, setT] = useState("");
  return (
    <div className="mt-2 pt-2 border-t border-slate-100">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] text-slate-400">Créneaux fixes :</span>
        {slots.map((x) => (
          <span key={x} className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-full px-2 py-0.5 text-xs" data-testid={`manual-slot-${day}-${x}`}>
            {x}<button type="button" onClick={() => onRemove(x)}><Trash2 className="w-3 h-3" /></button>
          </span>
        ))}
        <input type="time" data-testid={`manual-input-${day}`} value={t} onChange={(e) => setT(e.target.value)} className="bg-slate-50 border border-slate-200 rounded-lg px-2 py-0.5 text-xs" />
        <Button type="button" size="sm" variant="outline" data-testid={`manual-add-${day}`} onClick={() => { onAdd(t); setT(""); }} className="h-6 text-xs border-slate-200 px-2">Ajouter</Button>
      </div>
    </div>
  );
}

function AbsencesCard() {
  const [list, setList] = useState([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [reason, setReason] = useState("");
  const [who, setWho] = useState("");
  const [kind, setKind] = useState("conges");
  const [team, setTeam] = useState([]);
  const load = () => api.get("/absences").then((r) => setList(r.data || [])).catch(() => {});
  useEffect(() => { load(); api.get("/team/members").then((r) => setTeam(r.data || [])).catch(() => {}); }, []);
  const add = async () => {
    if (!start || !end) return toast.error("Renseignez les dates de début et de fin");
    if (end < start) return toast.error("La date de fin doit suivre la date de début");
    try { await api.post("/absences", { start, end, reason, user_id: who || null, kind }); setStart(""); setEnd(""); setReason(""); load(); toast.success("Absence ajoutée"); }
    catch { toast.error("Ajout impossible"); }
  };
  const KINDS = { conges: "Congés", maladie: "Maladie", formation: "Formation", autre: "Autre" };
  const name = (id) => (id ? team.find((m) => m.id === id)?.name || "Collaborateur" : "Toute l'entreprise");
  const del = async (id) => { try { await api.delete(`/absences/${id}`); load(); } catch {} };
  return (
    <Card title="Absences & vacances" testid="booking-absences">
      <p className="text-xs text-slate-500 mb-3">Une absence de toute l'entreprise bloque la réservation en ligne. Une absence d'un collaborateur bloque ses créneaux dans l'agenda (alerte de conflit lorsqu'un RDV lui est attribué).</p>
      <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-2 items-end mb-3">
        <div className="space-y-1"><Label className="text-xs text-slate-500">Qui</Label><select data-testid="absence-user" value={who} onChange={(e) => setWho(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2 py-2 text-sm"><option value="">Toute l'entreprise</option>{team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select></div>
        <div className="space-y-1"><Label className="text-xs text-slate-500">Type</Label><select data-testid="absence-kind" value={kind} onChange={(e) => setKind(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2 py-2 text-sm">{Object.entries(KINDS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
        <div className="space-y-1"><Label className="text-xs text-slate-500">Du</Label><input type="date" data-testid="absence-start" value={start} onChange={(e) => setStart(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2 py-2 text-sm" /></div>
        <div className="space-y-1"><Label className="text-xs text-slate-500">Au</Label><input type="date" data-testid="absence-end" value={end} onChange={(e) => setEnd(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2 py-2 text-sm" /></div>
        <div className="space-y-1"><Label className="text-xs text-slate-500">Motif (facultatif)</Label><Input data-testid="absence-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="bg-slate-50 border-slate-200" /></div>
        <Button type="button" data-testid="absence-add" onClick={add} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"><Plus className="w-4 h-4 mr-1" />Ajouter</Button>
      </div>
      <div className="space-y-1.5">
        {list.map((a) => (
          <div key={a.id} data-testid={`absence-${a.id}`} className="flex items-center justify-between text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            <span className="text-slate-700">{a.start} → {a.end} · {name(a.user_id)}{a.kind ? ` · ${KINDS[a.kind] || a.kind}` : ""}{a.reason ? ` · ${a.reason}` : ""}</span>
            <button type="button" data-testid={`absence-del-${a.id}`} onClick={() => del(a.id)} className="text-red-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {list.length === 0 && <p className="text-xs text-slate-400">Aucune absence enregistrée.</p>}
      </div>
    </Card>
  );
}

function GalleryCard() {
  const [media, setMedia] = useState([]);
  const [busy, setBusy] = useState(false);
  const [rdvPhotos, setRdvPhotos] = useState([]);
  const [showRdv, setShowRdv] = useState(false);
  const load = () => api.get("/showcase-media").then((r) => setMedia(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (files) => {
    setBusy(true);
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const type = file.type.startsWith("video") ? "video" : "image";
        await api.post("/showcase-media", { type, url: data.url, path: data.path, source: "upload", order: media.length });
      } catch { toast.error("Échec de l'envoi d'un média"); }
    }
    setBusy(false); load();
  };
  const del = async (id) => { try { await api.delete(`/showcase-media/${id}`); load(); } catch {} };
  const openRdv = async () => {
    setShowRdv(true);
    try { const { data } = await api.get("/gallery"); setRdvPhotos(data.photos || []); } catch {}
  };
  const addFromRdv = async (p) => {
    try { await api.post("/showcase-media", { type: "image", url: p.url, source: "appointment", caption: p.client_name, order: media.length }); toast.success("Ajouté à la vitrine"); load(); }
    catch { toast.error("Ajout impossible"); }
  };

  return (
    <Card title="Galerie photos & vidéos (vitrine)" testid="booking-gallery">
      <p className="text-xs text-slate-500 mb-3">Sélectionnez les médias affichés sur votre page publique. Importez des photos/vidéos dédiées ou piochez dans les photos avant/après de vos RDV.</p>
      <div className="flex flex-wrap gap-2 mb-4">
        <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-black font-semibold text-sm cursor-pointer" data-testid="media-upload-label">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Importer un média
          <input type="file" accept="image/*,video/*" multiple className="hidden" data-testid="media-upload-input" onChange={(e) => e.target.files && upload(e.target.files)} />
        </label>
        <Button type="button" variant="outline" data-testid="media-from-rdv" onClick={openRdv} className="border-slate-200"><ImageIcon className="w-4 h-4 mr-1" />Depuis mes RDV</Button>
      </div>

      {showRdv && (
        <div className="border border-slate-200 rounded-lg p-3 mb-4" data-testid="rdv-picker">
          <div className="flex items-center justify-between mb-2"><span className="text-xs font-medium text-slate-700">Photos de vos RDV</span><button type="button" onClick={() => setShowRdv(false)} className="text-xs text-slate-500">Fermer</button></div>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 max-h-60 overflow-y-auto">
            {rdvPhotos.map((p, i) => (
              <button type="button" key={i} onClick={() => addFromRdv(p)} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 group">
                <img src={fileSrc(p.url)} alt="" className="w-full h-full object-cover" />
                <span className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-xs"><Plus className="w-5 h-5" /></span>
              </button>
            ))}
            {rdvPhotos.length === 0 && <p className="text-xs text-slate-400 col-span-full">Aucune photo de RDV disponible.</p>}
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {media.map((m) => (
          <div key={m.id} data-testid={`media-${m.id}`} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-100 group">
            {m.type === "video" ? <video src={fileSrc(m.url || m.path)} className="w-full h-full object-cover" /> : <img src={fileSrc(m.url || m.path)} alt="" className="w-full h-full object-cover" />}
            <button type="button" data-testid={`media-del-${m.id}`} onClick={() => del(m.id)} className="absolute top-1 right-1 bg-black/70 rounded-full p-1 text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        {media.length === 0 && <p className="text-xs text-slate-400 col-span-full">Aucun média dans votre vitrine.</p>}
      </div>
    </Card>
  );
}

function ReviewsCard({ publicUrl }) {
  const [list, setList] = useState([]);
  const [author, setAuthor] = useState("");
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const load = () => api.get("/reviews").then((r) => setList(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!author.trim()) return toast.error("Nom requis");
    try { await api.post("/reviews", { author, rating, text, status: "published" }); setAuthor(""); setText(""); setRating(5); load(); toast.success("Avis ajouté"); }
    catch { toast.error("Ajout impossible"); }
  };
  const setStatus = async (r, status) => { try { await api.put(`/reviews/${r.id}`, { author: r.author, rating: r.rating, text: r.text || "", status }); load(); } catch {} };
  const del = async (id) => { try { await api.delete(`/reviews/${id}`); load(); } catch {} };
  const STAT = { published: "Publié", pending: "En attente", hidden: "Masqué" };

  return (
    <>
      <Card title="Ajouter un avis" testid="booking-review-add">
        <div className="flex items-center gap-1 mb-2">
          {[1, 2, 3, 4, 5].map((i) => <button key={i} type="button" data-testid={`radd-star-${i}`} onClick={() => setRating(i)}><Star className={`w-6 h-6 ${i <= rating ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} /></button>)}
        </div>
        <div className="grid sm:grid-cols-2 gap-2 mb-2">
          <Input data-testid="radd-author" placeholder="Nom du client" value={author} onChange={(e) => setAuthor(e.target.value)} className="bg-slate-50 border-slate-200" />
        </div>
        <Textarea data-testid="radd-text" placeholder="Texte de l'avis" value={text} onChange={(e) => setText(e.target.value)} className="bg-slate-50 border-slate-200 mb-2" />
        <Button type="button" data-testid="radd-submit" onClick={add} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"><Plus className="w-4 h-4 mr-1" />Ajouter</Button>
        <p className="text-[11px] text-slate-400 mt-2">Vos clients peuvent aussi déposer un avis directement depuis votre page publique ({publicUrl || "votre lien"}). Ils arrivent alors « En attente » pour modération.</p>
      </Card>

      <Card title="Avis reçus" testid="booking-reviews-list">
        <div className="space-y-2">
          {list.map((r) => (
            <div key={r.id} data-testid={`review-${r.id}`} className="border border-slate-200 rounded-lg p-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="flex">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`w-3.5 h-3.5 ${i <= r.rating ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} />)}</span>
                    <span className="text-sm font-medium text-slate-800">{r.author}</span>
                    {r.source === "public" && <span className="text-[10px] bg-blue-50 text-blue-600 border border-blue-200 rounded-full px-1.5">public</span>}
                  </div>
                  {r.text && <p className="text-sm text-slate-600 mt-1">{r.text}</p>}
                </div>
                <button type="button" data-testid={`review-del-${r.id}`} onClick={() => del(r.id)} className="text-red-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="mt-2">
                <Select value={r.status} onValueChange={(v) => setStatus(r, v)}>
                  <SelectTrigger data-testid={`review-status-${r.id}`} className="w-40 h-8 text-xs bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STAT).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
          {list.length === 0 && <p className="text-xs text-slate-400">Aucun avis pour le moment.</p>}
        </div>
      </Card>
    </>
  );
}

function FaqCard() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [a, setA] = useState("");
  const load = () => api.get("/faqs").then((r) => setList(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!q.trim() || !a.trim()) return toast.error("Question et réponse requises");
    try { await api.post("/faqs", { question: q, answer: a, order: list.length }); setQ(""); setA(""); load(); toast.success("Question ajoutée"); }
    catch { toast.error("Ajout impossible"); }
  };
  const del = async (id) => { try { await api.delete(`/faqs/${id}`); load(); } catch {} };
  return (
    <Card title="Questions fréquentes (FAQ)" testid="booking-faq">
      <p className="text-xs text-slate-500 mb-3">Ces questions/réponses s'affichent sur votre page publique.</p>
      <div className="space-y-2 mb-4">
        <Input data-testid="faq-question" placeholder="Question" value={q} onChange={(e) => setQ(e.target.value)} className="bg-slate-50 border-slate-200" />
        <Textarea data-testid="faq-answer" placeholder="Réponse" value={a} onChange={(e) => setA(e.target.value)} className="bg-slate-50 border-slate-200" />
        <Button type="button" data-testid="faq-add" onClick={add} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"><Plus className="w-4 h-4 mr-1" />Ajouter</Button>
      </div>
      <div className="space-y-2">
        {list.map((f) => (
          <div key={f.id} data-testid={`faq-${f.id}`} className="border border-slate-200 rounded-lg p-3">
            <div className="flex items-start justify-between gap-2">
              <div><p className="text-sm font-medium text-slate-800">{f.question}</p><p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{f.answer}</p></div>
              <button type="button" data-testid={`faq-del-${f.id}`} onClick={() => del(f.id)} className="text-red-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="text-xs text-slate-400">Aucune question pour le moment.</p>}
      </div>
    </Card>
  );
}

function SaveBtn({ onClick, saving, label = "Enregistrer", inline }) {
  return (
    <div className={inline ? "" : "pb-2"}>
      <Button onClick={onClick} disabled={saving} data-testid="booking-save-bottom" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold w-full sm:w-auto">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}{label}
      </Button>
    </div>
  );
}

function Card({ title, testid, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4" data-testid={testid}>
      <div className="font-heading font-semibold text-slate-900 mb-3">{title}</div>
      {children}
    </div>
  );
}
